import React, { useEffect, useState } from 'react';
import './CreditsBadge.css';
import { getAccountCredits, CREDITS_CHANGED_EVENT } from '../../services/api';

const REFRESH_INTERVAL_MS = 60_000;

function CoinIcon() {
  return (
    <svg className="credits-badge__coin" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8.8" className="credits-badge__coin-rim" strokeWidth="1.6" fill="none" />
      <path
        className="credits-badge__coin-spark"
        d="M12 6.4c.55 3.05 2.1 4.6 5.15 5.15-3.05.55-4.6 2.1-5.15 5.15-.55-3.05-2.1-4.6-5.15-5.15 3.05-.55 4.6-2.1 5.15-5.15Z"
      />
    </svg>
  );
}

function CreditsBadge({ authenticated }) {
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!authenticated) {
      setCredits(null);
      return undefined;
    }

    let cancelled = false;

    const refresh = async () => {
      try {
        const status = await getAccountCredits();
        if (!cancelled) setCredits(status);
      } catch {
        // Header decoration only — never surface errors here.
      }
    };

    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    const handleRefreshSignal = () => refresh();
    window.addEventListener('focus', handleRefreshSignal);
    window.addEventListener(CREDITS_CHANGED_EVENT, handleRefreshSignal);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', handleRefreshSignal);
      window.removeEventListener(CREDITS_CHANGED_EVENT, handleRefreshSignal);
    };
  }, [authenticated]);

  if (!authenticated || !credits) {
    return null;
  }

  if (!credits.enforced) {
    return (
      <div className="credits-badge-wrap">
        <div className="credits-badge credits-badge--network-text" aria-hidden="true">
          <CoinIcon />
          <span className="credits-badge__value credits-badge__value--infinity">∞</span>
        </div>
        <span className="credits-badge__hover-note">
          <span className="credits-badge__hover-note-title">In network: unlimited credits</span>
          Your usage is covered by the lab while you are on the network, so no
          weekly credit limit applies.
        </span>
        <span className="visually-hidden-credits">In network: unlimited credits</span>
      </div>
    );
  }

  const remaining = Math.max(0, Math.floor(credits.remaining));
  const ratioLeft = credits.limit > 0 ? remaining / credits.limit : 0;
  const level = remaining <= 0 ? 'empty' : ratioLeft <= 0.1 ? 'low' : 'ok';
  const resetsAt = credits.resets_at ? new Date(credits.resets_at) : null;
  const resetsText = resetsAt && !Number.isNaN(resetsAt.getTime())
    ? ` Your allowance resets ${resetsAt.toLocaleDateString('en-US', { weekday: 'long' })}.`
    : '';

  return (
    <div className="credits-badge-wrap">
      <div className={`credits-badge credits-badge--${level}`}>
        <CoinIcon />
        <span className="credits-badge__value">{remaining.toLocaleString()}</span>
      </div>
      <span className="credits-badge__hover-note">
        <span className="credits-badge__hover-note-title">
          {`${remaining.toLocaleString()} of ${credits.limit.toLocaleString()} weekly credits left`}
        </span>
        {`You are out of network, so sessions draw from your weekly credit allowance.${resetsText}`}
      </span>
    </div>
  );
}

export default CreditsBadge;
